import type { OrderStatus } from '../../../types/order';
import type { InvoiceCoverageState } from '../../../types/invoiceCoverage';

/**
 * The eight steps of the ZK card (makieta „ZK w pełnym FIFO”, decision
 * 2026-10-04). A step is a view on the order status; statuses stay
 * informational (WP3) and never block — the card only guides the next action.
 *
 * `owner` is who usually does the step („Wykonuje: …” in the step banner), not
 * a permission: the backend capabilities decide who may act.
 */
export type OrderProcessStepKey =
  | 'DRAFT'
  | 'OFFER'
  | 'CONFIRMED'
  | 'FULFILLMENT'
  | 'READY'
  | 'DELIVERY'
  | 'DELIVERED'
  | 'INVOICE';

export type OrderProcessOwner = 'SALES' | 'OPERATIONS' | 'ADMIN';

export interface OrderProcessStep {
  key: OrderProcessStepKey;
  /** The status this step stands for (PARTIALLY_DELIVERED also maps to DELIVERY). */
  status: OrderStatus;
  owner: OrderProcessOwner;
}

export const ORDER_PROCESS_STEPS: readonly OrderProcessStep[] = [
  { key: 'DRAFT', status: 'NEW_INQUIRY', owner: 'SALES' },
  { key: 'OFFER', status: 'QUOTE_SENT', owner: 'SALES' },
  { key: 'CONFIRMED', status: 'CONFIRMED', owner: 'SALES' },
  { key: 'FULFILLMENT', status: 'IN_FULFILLMENT', owner: 'OPERATIONS' },
  { key: 'READY', status: 'READY_FOR_DISPATCH', owner: 'OPERATIONS' },
  { key: 'DELIVERY', status: 'IN_DELIVERY', owner: 'OPERATIONS' },
  { key: 'DELIVERED', status: 'DELIVERED', owner: 'SALES' },
  { key: 'INVOICE', status: 'INVOICED', owner: 'ADMIN' },
];

const STEP_BY_STATUS: Partial<Record<OrderStatus, OrderProcessStepKey>> = {
  NEW_INQUIRY: 'DRAFT',
  QUOTE_SENT: 'OFFER',
  CONFIRMED: 'CONFIRMED',
  IN_FULFILLMENT: 'FULFILLMENT',
  READY_FOR_DISPATCH: 'READY',
  IN_DELIVERY: 'DELIVERY',
  PARTIALLY_DELIVERED: 'DELIVERY',
  DELIVERED: 'DELIVERED',
  INVOICED: 'INVOICE',
};

export const getOrderProcessStepIndex = (key: OrderProcessStepKey): number =>
  ORDER_PROCESS_STEPS.findIndex((step) => step.key === key);

export const getOrderProcessStepForStatus = (status: string | null | undefined): OrderProcessStepKey | null =>
  (status && STEP_BY_STATUS[status as OrderStatus]) || null;

export interface OrderProcessPosition {
  /** The step the order is at; for Wstrzymane / Anulowane the last step before it. */
  current: OrderProcessStepKey;
  currentIndex: number;
  /** Set when the order is on hold or cancelled: the rail shows it, the step stays. */
  interruptedBy: 'ON_HOLD' | 'CANCELLED' | null;
}

interface StatusHistoryLike {
  status?: string | null;
  changedAt?: string | null;
}

/**
 * Where the order is on the rail. Wstrzymane / Anulowane are not steps: the
 * card keeps the last progress step from the status history (newest first by
 * `changedAt`) and marks the interruption.
 */
export function getOrderProcessPosition(
  status: string | null | undefined,
  statusHistory: readonly StatusHistoryLike[] = [],
): OrderProcessPosition {
  const direct = getOrderProcessStepForStatus(status);
  if (direct) {
    return { current: direct, currentIndex: getOrderProcessStepIndex(direct), interruptedBy: null };
  }
  const interruptedBy = status === 'ON_HOLD' || status === 'CANCELLED' ? status : null;
  const previous = [...statusHistory]
    .sort((a, b) => new Date(b.changedAt || 0).getTime() - new Date(a.changedAt || 0).getTime())
    .map((entry) => getOrderProcessStepForStatus(entry.status))
    .find((key): key is OrderProcessStepKey => Boolean(key));
  const current = previous || 'DRAFT';
  return { current, currentIndex: getOrderProcessStepIndex(current), interruptedBy };
}

/**
 * Steps the rail lets the operator open: every finished step (read-only
 * preview) and the current one. „Faktura” is also open from „Dostarczone”,
 * where its action — uploading the sales invoice — already applies, and
 * „Oferta” from „Szkic”: the offer is prepared and sent there, and only the
 * sending (or „Oznacz ofertę jako wysłaną”) moves the order to „Oferta wysłana”.
 */
export function canOpenOrderProcessStep(position: OrderProcessPosition, key: OrderProcessStepKey): boolean {
  const index = getOrderProcessStepIndex(key);
  if (index <= position.currentIndex) return true;
  return (key === 'INVOICE' && position.current === 'DELIVERED')
    || (key === 'OFFER' && position.current === 'DRAFT' && !position.interruptedBy);
}

/**
 * Facts that change the wording of a step (never its meaning): customer
 * self-pickup („Do odbioru przez klienta” instead of „W dostawie”), part of
 * the goods already delivered (the step is named like the status pill,
 * „Częściowo dostarczone”), every line on the warehouse (W realizacji waits
 * for nothing) and the confirmation already sent (Potwierdzone no longer asks
 * to send it).
 */
export interface OrderProcessCopyFacts {
  pickup: boolean;
  partiallyDelivered: boolean;
  allInStock: boolean;
  confirmationSent: boolean;
}

export type OrderProcessCopyField = 'label' | 'title' | 'description' | 'banner';

/** `orders:` key of a step text, with the variant for the given facts. */
export function getOrderProcessStepCopyKey(
  key: OrderProcessStepKey,
  field: OrderProcessCopyField,
  facts: Partial<OrderProcessCopyFacts> = {},
): string {
  // Before pickup: the pill says „Częściowo dostarczone” for a pickup order too.
  if (key === 'DELIVERY' && facts.partiallyDelivered) return `orders:process.steps.DELIVERY_PARTIAL.${field}`;
  if (key === 'DELIVERY' && facts.pickup) return `orders:process.steps.DELIVERY_PICKUP.${field}`;
  if (key === 'FULFILLMENT' && facts.allInStock && (field === 'description' || field === 'banner')) {
    return `orders:process.steps.FULFILLMENT.${field}Ready`;
  }
  if (key === 'CONFIRMED' && facts.confirmationSent && field === 'banner') return 'orders:process.steps.CONFIRMED.bannerSent';
  return `orders:process.steps.${key}.${field}`;
}

/** A finished step (left of the current one) opens as a read-only preview. */
export const isOrderProcessStepReadOnly = (position: OrderProcessPosition, key: OrderProcessStepKey): boolean =>
  getOrderProcessStepIndex(key) < position.currentIndex;

/**
 * Header pill (makieta `pillLabel`): the status label, with the three wordings
 * of the end of the process. Returns an `orders:` translation key.
 * `invoiceCoverage` is the ZK's existing invoice coverage state (WP11): a
 * delivered order already partly invoiced waits for the rest of the invoice.
 */
export function getOrderProcessPillKey(
  status: string | null | undefined,
  options: { pickup?: boolean; invoiceCoverage?: InvoiceCoverageState | null } = {},
): string {
  // Self-pickup: the same status, told as „Do odbioru przez klienta” (labels only, no new status).
  if (status === 'IN_DELIVERY' && options.pickup) return 'orders:process.pill.IN_DELIVERY_PICKUP';
  if (status === 'DELIVERED' && options.invoiceCoverage === 'PARTIAL') return 'orders:process.pill.DELIVERED_PARTIALLY_INVOICED';
  if (status === 'DELIVERED') return 'orders:process.pill.DELIVERED';
  if (status === 'INVOICED') return 'orders:process.pill.INVOICED';
  if (status === 'PARTIALLY_DELIVERED') return 'orders:process.pill.PARTIALLY_DELIVERED';
  return `orders:statusOptions.${status || 'NEW_INQUIRY'}`;
}

// „Utwórz WZ i przejdź do dostawy” (makieta `issueWz`): the first released WZ
// moves the order to „W dostawie” from these statuses; a later WZ, a WZ
// saved as a draft or any other status keeps the status (FF.10).
const WZ_MOVES_TO_DELIVERY_FROM: readonly string[] = ['CONFIRMED', 'IN_FULFILLMENT', 'READY_FOR_DISPATCH'];

export function getStatusAfterWzIssued(status: string | null | undefined, released: boolean): OrderStatus | null {
  return released && status && WZ_MOVES_TO_DELIVERY_FROM.includes(status) ? 'IN_DELIVERY' : null;
}
